/* eslint-disable no-extra-boolean-cast */
import loadable from '@loadable/component';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import {
  setAddress,
  setPlaceId,
  setPosition,
  setProductId,
  setSelectedShop,
} from '../actions/course';
import {
  AnnuaireFonts,
  colors,
  fonts,
  radii,
  containerStyle,
  sectionPaddingStyle,
} from '../components/annuaire/designSystem';
import ShopBreadcrumb from '../components/workshopStandalone/ShopBreadcrumb';
import ShopHero from '../components/workshopStandalone/ShopHero';
import TrustBanner from '../components/workshopStandalone/TrustBanner';
import ShopDescription from '../components/workshopStandalone/ShopDescription';
import ShopServices from '../components/workshopStandalone/ShopServices';
import ShopInfosPratiques from '../components/workshopStandalone/ShopInfosPratiques';
import ShopPrestations from '../components/workshopStandalone/ShopPrestations';
import { isFixerLiteEnabled } from '../annuaire/bookingState';
import ShopMap from '../components/workshopStandalone/ShopMap';
import ShopReviews from '../components/workshopStandalone/ShopReviews';
import ShopFAQ from '../components/workshopStandalone/ShopFAQ';
import SimilarShops from '../components/workshopStandalone/SimilarShops';
import MobileCTABar from '../components/workshopStandalone/MobileCTABar';
import ShopJsonLd from '../components/workshopStandalone/ShopJsonLd';
import ReservationBox from '../components/workshopStandalone/ReservationBox';
import { normalizeShop, getCityUrl, isDev } from '../utils/utils';
import * as api from '../utils/api';
import { ANNUAIRE_ROOT, garderQuery } from '../annuaire/resolvePath';
import { decideDeadAnnuairePath } from '../annuaire/resolveClient';
import CarteRendezVous from '../components/workshopStandalone/CarteRendezVous';
import WriteToFixerModal from '../components/workshopStandalone/WriteToFixerModal';
import useRdvAtelier from '../components/workshopStandalone/useRdvAtelier';
import { reprendreBrouillon } from '../annuaire/tunnel/brouillon';
import { retenirVia } from '../annuaire/tunnel/via';
import NotFound from './NotFound';

// Le tunnel ne s'ouvre qu'au clic, jamais dans le HTML servi : il sort du
// paquet principal et se charge à la demande.
const Tunnel = loadable(() => import('../annuaire/tunnel/Tunnel'), {
  ssr: false,
});

// ---------------------------------------------------------------------------
// Styled wrappers
// ---------------------------------------------------------------------------

const PageWrapper = styled.div`
  background-color: #ffffff;
  font-family: ${fonts.body};
  min-height: 100vh;
`;

const BookingSection = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  background-color: ${colors.white};
`;

const BookingContainer = styled.div`
  ${containerStyle}
  display: flex;
  justify-content: center;
`;

const BookingCard = styled.div`
  width: 100%;
  max-width: 480px;
  background-color: ${colors.white};
  border-radius: ${radii.card};
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WorkshopStandalone({ shopData }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [rawShop, setRawShop] = useState(shopData || {});
  // 404 | 410 quand le chemin n'est pas servi. `src/index.jsx` monte l'app avec
  // `render()` et non `hydrate()` : sans cet état, le client écraserait la page
  // 404/410 rendue par le SSR par une redirection vers la page ville, et
  // l'utilisateur verrait le contraire de ce que voit Google.
  const [missingCode, setMissingCode] = useState(null);
  const [product, setProduct] = useState('1');
  // Le tunnel de réservation (lot 1). Rouvert d'office quand un brouillon
  // revient d'un aller-retour Google (voir `annuaire/tunnel/brouillon.js`).
  const [tunnelOuvert, setTunnelOuvert] = useState(false);
  const [brouillon, setBrouillon] = useState(null);
  const [preselection, setPreselection] = useState(null);
  // FIXERLIGHT-049 — « Écrire au réparateur » (lot indépendant du tunnel de
  // réservation ci-dessus, contrat api § 2).
  const [writeModalOpen, setWriteModalOpen] = useState(false);
  const clientId = useSelector((s) => (s.client && s.client.id) || null);
  const { dept, city, slug } = useParams();

  // Normalize: flatten details array → object for new components
  const shop = useMemo(() => normalizeShop(rawShop), [rawShop]);
  const details = shop.details || {};
  const seo = details.seo || {};
  const similarShops = shop.similar_shops || [];

  // --- Data fetching (client-side) ---
  // `isCancelled` vient de l'effet : la résolution enchaîne deux requêtes, donc
  // une navigation SPA entre deux fiches (les liens « ateliers similaires ») peut
  // faire atterrir la réponse de la page A alors que la page B est montée. Sans
  // cette garde, un `setMissingCode(404)` tardif épinglerait une fiche VIVANTE
  // sur la page 404, sans rien pour l'en sortir.
  const getShopByPath = async (dept, city, slug, isCancelled) => {
    setLoading(true);
    setMissingCode(null);
    try {
      const shopItem = await api.get(`/shops/by-path/${dept}/${city}/${slug}`);
      if (isCancelled()) return;

      if (!shopItem || !shopItem.id) {
        // Fiche absente : on applique la MÊME résolution que le SSR — la
        // Redirection décide de la destination et du code, pas cette scène.
        const decision = await decideDeadAnnuairePath(
          `${ANNUAIRE_ROOT}/${dept}/${city}/${slug}`,
          getCityUrl(dept, city),
        );
        if (isCancelled()) return;

        if (decision.type === 'redirect') {
          // Comme le 301 du serveur : la query suit la fiche (`?via=`, UTM).
          history.replace(
            garderQuery(decision.location, history.location.search),
          );
          return;
        }
        if (decision.type === 'gone' || decision.type === 'notFound') {
          setMissingCode(decision.type === 'gone' ? 410 : 404);
          return;
        }
        // 'unavailable' : l'api ne répond pas. Le SSR sert un 503 ; le client n'a
        // pas de code HTTP à poser, et n'a surtout pas à prétendre que la page
        // n'existe pas. On laisse l'écran en l'état, comme avant ce lot.
        console.error(
          '[annuaire] résolution client indisponible',
          dept,
          city,
          slug,
        );
        return;
      }
      setRawShop(shopItem);
    } catch (error) {
      console.error('Failed to fetch shop:', error);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    if (!shopData) {
      getShopByPath(dept, city, slug, isCancelled);
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [dept, city, slug, shopData]);

  // --- SEO Meta Tags ---
  // Use enriched SEO fields from Airtable, fallback to existing i18n logic
  const metaTitle =
    seo.meta_title_seo ||
    (shop.city && shop.postal_code
      ? t('standalone.meta.title', {
          name: shop.name,
          city: `${shop.city} ${shop.postal_code}`,
        })
      : shop.name || '');

  const metaDescription =
    seo.meta_description_seo ||
    (shop.city && shop.postal_code
      ? t('standalone.meta.description', {
          name: shop.name,
          city: `${shop.city} ${shop.postal_code}`,
        })
      : shop.name || '');

  // OG image: use S3 main photo if available
  const ogImage = (details.photos && details.photos.s3_main) || '';

  // --- Booking Flow (preserved from original) ---
  const createAppointment = () => {
    dispatch(setPlaceId(''));
    dispatch(setAddress(shop.address));
    dispatch(setPosition(shop.address, ''));
    dispatch(setProductId(product));
    dispatch(setSelectedShop(shop));
    history.push('/reparation/symptoms');
  };

  /*
   * Le routage de la fiche (FIXERLIGHT-020), et l'ordre compte.
   *
   *   1. la fiche a un compte Fixer Lite → parcours Fixer Lite ;
   *   2. sinon elle est réservable au sens RJ → dépôt en atelier partenaire,
   *      comportement inchangé ;
   *   3. sinon → fiche informative, sans réservation.
   *
   * Le cas 1 prime sur le cas 2 quand les deux sont vrais : un compte signifie
   * que le réparateur tient son PROPRE agenda, alors que le parcours RJ lit
   * `$shop->fixer->zone_id`, qui n'existe pas hors des sept zones. Lui proposer
   * le dépôt enverrait le client dans un tunnel sans zone.
   *
   * `fixer_lite.enabled` dit « joignable », pas « existe en base » : l'api le
   * met à faux quand /v2 n'est pas monté dans l'environnement. C'est ce qui
   * évite d'ouvrir un tunnel dont la première requête répondrait 404.
   */
  // `?fixerlite=1` force le parcours en développement, et seulement là : aucun
  // compte atelier n'existe encore, en production comme en staging (22/09).
  const forceFixerLite =
    isDev() &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('fixerlite') === '1';
  const isFixerLite = forceFixerLite || isFixerLiteEnabled(shop);
  const isBookable = !isFixerLite && !!(shop && shop.bookable);
  const rdv = useRdvAtelier(shop, isFixerLite);

  useEffect(() => {
    // La page reste montée d'une fiche à l'autre (ateliers similaires) : ni
    // l'horaire cliqué ni le brouillon de la fiche précédente ne la suivent.
    setPreselection(null);
    setBrouillon(null);
    if (!isFixerLite || !shop.id) return;
    // Ouverte par le lien ou le QR du réparateur (`?via=`, FIXERLIGHT-062) :
    // le marqueur est retenu pour la demande, qui peut partir après un
    // aller-retour de connexion revenu sans lui.
    retenirVia(shop.id, history.location.search);
    // « Prendre rendez-vous » depuis une carte du listing (lot 3) : la fiche
    // s'ouvre avec le tunnel déjà ouvert. Le paramètre sort de l'adresse,
    // sinon un rechargement ou un retour rouvrirait le tunnel.
    const params = new URLSearchParams(history.location.search);
    const depuisListing = params.get('rdv') === '1';
    if (depuisListing) {
      params.delete('rdv');
      const reste = params.toString();
      history.replace({
        pathname: history.location.pathname,
        search: reste ? `?${reste}` : '',
        hash: history.location.hash,
      });
    }
    const repris = reprendreBrouillon(shop.id, clientId);
    if (repris) {
      setBrouillon(repris);
      setTunnelOuvert(true);
    } else if (depuisListing) {
      setTunnelOuvert(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFixerLite, shop.id]);

  const ouvrirTunnel = (choix = null) => {
    setBrouillon(null);
    setPreselection(choix);
    setTunnelOuvert(true);
  };

  // --- Render ---
  // Chemin mort : on rend la même page que le SSR (404 ou 410). `staticContext`
  // est absent côté client, `NotFound` ne pose alors aucun statut.
  if (missingCode) return <NotFound code={missingCode} />;

  return (
    <PageWrapper>
      <AnnuaireFonts />

      {/* SEO: Helmet meta tags */}
      <Helmet
        title={metaTitle}
        meta={[
          { name: 'description', content: metaDescription },
          { property: 'og:title', content: metaTitle },
          { property: 'og:description', content: metaDescription },
          { property: 'og:type', content: 'business.business' },
          ...(ogImage ? [{ property: 'og:image', content: ogImage }] : []),
        ]}
      />

      {/* SEO: JSON-LD structured data (LocalBusiness, BreadcrumbList, FAQPage) */}
      <ShopJsonLd shop={shop} />

      {/* Breadcrumb: Accueil / Annuaire / Dept / Ville / Nom */}
      <ShopBreadcrumb shop={shop} />

      {/* Tête : photos, avis, H1, adresse, services ; pour un atelier Fixer
          Lite, la carte de rendez-vous et les prestations à côté. */}
      <ShopHero
        shop={shop}
        carte={
          isFixerLite ? (
            <CarteRendezVous
              rdv={rdv}
              onPrendreRendezVous={ouvrirTunnel}
              onEcrireAuReparateur={() => setWriteModalOpen(true)}
            />
          ) : null
        }>
        {isFixerLite && <ShopPrestations shop={shop} integre />}
      </ShopHero>

      {/* Trust banner: 3 stats (note, services count, statut) */}
      <TrustBanner shop={shop} />

      {/* Description: SEO intro text + points forts */}
      <ShopDescription shop={shop} />

      {/* Services: dark background, colored service cards grid */}
      <ShopServices shop={shop} />

      {/* Infos pratiques: horaires table + contact info */}
      <ShopInfosPratiques shop={shop} />

      {/* Map: Google Maps embed */}
      <ShopMap shop={shop} />

      {/* Reviews: aggregate rating + up to 3 review cards */}
      <ShopReviews shop={shop} />

      {/* FAQ: accordion on indigo background */}
      <ShopFAQ shop={shop} />

      {/* Similar shops: 3 shop cards from same city */}
      <SimilarShops similarShops={similarShops} city={shop.city} />

      {/* Cas 1 : le réparateur tient son propre agenda (FIXERLIGHT-020). */}
      {isFixerLite && tunnelOuvert && (
        <Tunnel
          shop={shop}
          brouillon={brouillon}
          preselection={preselection}
          onFermer={() => setTunnelOuvert(false)}
        />
      )}

      {/* FIXERLIGHT-049 — « Écrire au réparateur », indépendante du tunnel
          de réservation ci-dessus. */}
      {isFixerLite && (
        <WriteToFixerModal
          open={writeModalOpen}
          onClose={() => setWriteModalOpen(false)}
          shopId={shop.id}
          shopName={shop.name}
        />
      )}

      {/* Cas 2 : dépôt en atelier partenaire, comportement inchangé. */}
      {isBookable && (
        <BookingSection>
          <BookingContainer>
            <BookingCard>
              <ReservationBox
                {...shop}
                createAppointment={createAppointment}
                product={product}
                setProduct={setProduct}
              />
            </BookingCard>
          </BookingContainer>
        </BookingSection>
      )}

      {/* Barre basse mobile : « Prendre rendez-vous » et le téléphone (Fixer
          Lite), sinon « Appeler » et le site web. */}
      <MobileCTABar
        shop={shop}
        onPrendreRendezVous={isFixerLite ? () => ouvrirTunnel() : null}
        instant={rdv.instant}
        tel={rdv.tel}
      />
    </PageWrapper>
  );
}
